import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Search, Copy, ExternalLink, Rocket, ListChecks } from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { ActionButton, Button, IconButton } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

interface LandingPageItem {
  _id: string;
  internalName: string;
  slug: string;
  status: 'draft' | 'published';
  avatar?: { name: string; slug: string } | null;
  views: number;
  createdAt: string;
}

const AdminLandingPages: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [landingPages, setLandingPages] = useState<LandingPageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  const fetchLandingPages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '50',
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      });
      const response = await axios.get(`/landing-pages?${params}`);
      setLandingPages(response.data.landingPages);
    } catch (error) {
      console.error('Erro ao carregar landing pages:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus]);

  useEffect(() => {
    fetchLandingPages();
  }, [fetchLandingPages]);

  const handleDelete = async (lp: LandingPageItem) => {
    if (!(await confirm({ title: 'Excluir landing page', message: <>A landing page <strong className="text-gray-900">{lp.internalName}</strong> será excluída permanentemente e o link público deixará de funcionar.</>, confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/landing-pages/${lp._id}`);
      setLandingPages((prev) => prev.filter((item) => item._id !== lp._id));
      notifications.showSuccess('Landing page deletada!', 'A página foi removida com sucesso.');
    } catch (error: any) {
      console.error('Erro ao deletar landing page:', error);
      notifications.showError('Erro ao deletar', error.response?.data?.message || 'Tente novamente.');
    }
  };

  const publicUrl = (slug: string) => `${window.location.origin}/l/${slug}`;

  const { copy } = useCopyToClipboard();
  const copyLink = (slug: string) => copy(publicUrl(slug), slug);

  // Ações da linha: mesma marcação na tabela (desktop) e nos cards (mobile).
  const renderActions = (lp: (typeof landingPages)[number]) => (
    <div className="row-actions md:justify-end">
      <ActionButton
        action="edit"
        to={`/admin/landing-pages/${lp._id}/edit`}
      />
      <ActionButton
        action="delete"
        onClick={() => handleDelete(lp)}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.landingPages')}</h1>
              <p className="text-gray-600">{t('admin.landingPagesDesc')}</p>
            </div>
            <div className="flex flex-wrap gap-2 self-start">
              <Button variant="outline" to="/admin/landing-pages/leads">
                <ListChecks className="w-5 h-5" />
                <span>{t('admin.quizLeads')}</span>
              </Button>
              <Button to="/admin/landing-pages/new">
                <Plus className="w-5 h-5" />
                <span>{t('admin.landingPageNew')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search') as string}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="field pl-10 pr-4"
              />
            </div>
            <div className="lg:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="field field--select"
              >
                <option value="">Todos os status</option>
                <option value="draft">Rascunho</option>
                <option value="published">Publicada</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : landingPages.length === 0 ? (
            <div className="p-8 text-center">
              <Rocket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma landing page criada</h3>
              <Button to="/admin/landing-pages/new">
                {t('admin.landingPageNew')}
              </Button>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campanha</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avatar</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {landingPages.map((lp) => (
                    <tr key={lp._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{lp.internalName}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-0.5">
                          <span className="mr-1">/l/{lp.slug}</span>
                          <IconButton action="copyLink" value={publicUrl(lp.slug)} />
                          {lp.status === 'published' && (
                            <a
                              href={publicUrl(lp.slug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Abrir página"
                              aria-label="Abrir página"
                              className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${lp.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {lp.status === 'published' ? 'Publicada' : 'Rascunho'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lp.avatar?.name || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lp.views}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {renderActions(lp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-gray-200">
              {landingPages.map((lp) => (
                <li key={lp._id} className="p-4 space-y-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{lp.internalName}</div>
                    <div className="text-sm text-gray-500 truncate">/l/{lp.slug}</div>
                    <div className="mt-2 flex gap-2">
                      <Button variant="ghost" size="sm" className="flex-1"
                        type="button"
                        onClick={() => copyLink(lp.slug)}>
                        <Copy className="w-4 h-4 text-gray-500" />
                        Copiar link
                      </Button>
                      {lp.status === 'published' && (
                        <Button variant="ghost" size="sm" className="flex-1"
                          href={publicUrl(lp.slug)}
                          target="_blank"
                          rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                          Abrir página
                        </Button>
                      )}
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Status</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${lp.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {lp.status === 'published' ? 'Publicada' : 'Rascunho'}
                        </span>
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Avatar</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {lp.avatar?.name || '—'}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs text-gray-500">Views</dt>
                      <dd className="text-sm text-gray-700 break-words">
                        {lp.views}
                      </dd>
                    </div>
                  </dl>
                  {renderActions(lp)}
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLandingPages;
